import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MAX_PINNED_TURN_SKILLS } from '@aline/common';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import { isHotkey } from 'is-hotkey';
import { useSnackbar } from 'notistack';
import type { Descendant } from 'platejs';
import type { PlateEditor } from 'platejs/react';
import { useMount } from 'react-use';
import { AlineButton, AlineTooltip, Spacer } from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { ArrowUpIcon } from 'src/components/icons/ArrowUpIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { RevisionHistoryIcon } from 'src/components/icons/RevisionHistoryIcon';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { StopIcon } from 'src/components/icons/StopIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';
import { useFileStorageConnections, useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid4 } from 'uuid';
import { useSnapshot } from 'valtio';

import type { AlineMarkdownEditorRef } from './AlineMarkdownEditor';
import { AlineMarkdownEditor } from './AlineMarkdownEditor';
import { AddContextButton } from './AlineMarkdownEditor/add-context-button';
import { DOCUMENT_QUICK_TYPES } from './AlineMarkdownEditor/plugins/mention-entity-picker-helpers';
import { BaseSkillElementPlugin } from './AlineMarkdownEditor/plugins/skill-element-plugin';
import { createScopedSkillEntityKit } from './AlineMarkdownEditor/plugins/skill-entity-kit';
import { getChatDraftKey, readChatDraft, writeChatDraft } from './chat-draft-storage';
import { ChatSkillAudience, SHARED_CHAT_SKILL_SCOPES } from './chat-skill-audience';
import type { TMentionElement } from './ChatEditor';
import {
  ChatEditorHistory,
  ChatEditorMarkdownPlugin,
  deserializeMarkdownWithMentions,
  focusEditor,
  getEditorMarkdown,
  getIsEditorBlank,
  getIsEditorWithMarkdownApi,
  getIsMentionElement,
  isDocumentMentionElement,
  normalizeBlankMarkdown,
  SelectionMentionPastePlugin,
} from './ChatEditor';
import type { AccessStatusContentProps } from './ChatEditor/ChatEditorMention/MentionAccess';
import { renderAccessStatusContent } from './ChatEditor/ChatEditorMention/MentionAccess';
import {
  dedupePinnedSkills,
  getIsSkillScopeOffered,
  getPinnedSkillsFromEditor,
  insertSkillElement,
  SKILL_PIN_REFUSAL_MESSAGES,
} from './ChatEditor/ChatEditorSkill/utils';
import type { PinnedSkill } from './ChatEditor/ChatEditorSkill/utils';
import { useChatEditorDropzone } from '../chat-hooks/useChatEditorDropzone';
import { useStreamImprovePrompt } from '../chat-hooks/useImprovePrompt';
import { useInsertSelectionMentionV3 } from '../chat-hooks/useInsertSelectionMentionV3';
import type { InsertSelectionMentionV3Fn } from '../chat-hooks/useInsertSelectionMentionV3';
import {
  AccessRequestStatusContext,
  MentionEntityContext,
  useAccessRequestStatus,
} from '../chat-hooks/useMentionAccessContext';
import {
  extractDocumentChipIdsFromEditor,
  extractMentionEntityIdsFromEditor,
  useMentionEntitiesQuery,
} from '../chat-hooks/useMentionEntitiesQuery';
import { useRequestAccess } from '../chat-hooks/useRequestAccess';
import { $chatThreadState } from '../chat-state';

const TOO_MANY_SKILLS_MESSAGE = `${SKILL_PIN_REFUSAL_MESSAGES.limitReached} Remove one to send this message.`;
const SHARED_SKILLS_ONLY_MESSAGE = 'This chat can only use org and Community skills.';
const SEND_FAILED_MESSAGE = 'Could not send your message. Your draft was kept. Please try again.';
const chatBorderRadius = 16;
const useStyles = makeStyles<{ isDragging: boolean }>()((theme, { isDragging }) => ({
  buttonContainer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(0.5),
    margin: 0,
    padding: theme.spacing(1),
    width: '100%',
  },
  dropOverlay: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: chatBorderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
    zIndex: 10,
  },
  dropOverlayIcon: {
    color: theme.palette.primary.main,
    fontSize: 32,
  },
  dropOverlayText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  editorContainer: {
    '&:focus-within': {
      border: isDragging ? '1px solid transparent' : `1px solid ${theme.palette.primary.main}`,
    },
    border: isDragging ? '1px solid transparent' : `1px solid ${theme.appColors.rule}`,
    borderRadius: chatBorderRadius,
    boxSizing: 'border-box' as const,
    overflow: 'hidden',
    width: '100%',
  },
  iconButton: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    justifyContent: 'center',
    minHeight: 32,
    minWidth: 32,
    padding: theme.spacing(0.5),
  },
  root: {
    containerType: 'inline-size',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
}));

export interface ChatPanelInputRef {
  getDocumentChipIds: () => string[];
  /** Returns true if the mentions were inserted, false if the editor wasn't ready. */
  insertMentions: (mentions: TMentionElement[]) => boolean;
  insertSelectionMentionV3: InsertSelectionMentionV3Fn;
  /**
   * Adds a skill chip to the draft. Returns false if the editor wasn't ready; a refused
   * chip (a repeat, over the cap, or a `scope` this chat does not offer) still returns
   * true, with a message.
   */
  insertSkill: (skill: PinnedSkill, scope?: Apollo.AgentSkillScope) => boolean;
  removeDocumentMentions: (ids: string[]) => void;
  /**
   * Replaces the whole draft with this markdown, mention links included, and
   * leaves the caret at the end. Returns false if the editor wasn't ready.
   */
  setPromptDraft: (markdown: string) => boolean;
}

type EditorRef = React.RefObject<AlineMarkdownEditorRef | null>;
type SubmitButtonMessages = Pick<Apollo.AgentMessage, 'id' | 'role' | 'status'>[];

const ChatPanelInputEditor = forwardRef<
  ChatPanelInputRef,
  {
    draftKey: string;
    messages: SubmitButtonMessages;
    onNavigateToThread: (threadId: string) => void;
    onSubmit: (
      text: string,
      skills: PinnedSkill[],
      onConfirmed?: (confirmedThreadId?: string) => void
    ) => Promise<boolean>;
    threadId: UUID | null;
    className?: string;
    /** Fires after a thread is deleted from the history popover this input owns. */
    onThreadDeleted?: (threadId: UUID) => void;
    rootRef?: (element: HTMLDivElement | null) => void;
    visibleContext?: Apollo.AgentVisibleContextInput;
  }
>(function ChatPanelInputComponent(
  {
    className,
    draftKey,
    messages,
    onNavigateToThread,
    onSubmit,
    onThreadDeleted,
    rootRef,
    threadId,
    visibleContext,
  },
  ref
) {
  const dispatch = useNotificationDispatch();
  // Always defined for the history popover, and stable so it does not re-render on keystrokes.
  const handleThreadDeleted = useCallback(
    (deletedThreadId: UUID) => onThreadDeleted?.(deletedThreadId),
    [onThreadDeleted]
  );

  // Memoize plugins array - these never change but can't be module-level due to circular deps
  const chatEditorPlugins = useMemo(
    () => [
      SelectionMentionPastePlugin,
      ChatEditorMarkdownPlugin,
      ...createScopedSkillEntityKit(SHARED_CHAT_SKILL_SCOPES),
    ],
    []
  );

  // Track current text for enabling/disabling submit button, persisted to session storage
  const [currentText, setCurrentText] = useState(() => readChatDraft(draftKey));
  const hasText = Boolean(currentText.trim());
  const markdownEditorRef = useRef<AlineMarkdownEditorRef>(null);
  // Track when editor is ready (needed to re-extract document IDs after restoring from session storage)
  const [editorReady, setEditorReady] = useState(false);
  const [historyPopoverOpen, setHistoryPopoverOpen] = useState(false);
  const { improvePrompt, isImproving } = useImprovePrompt(markdownEditorRef);

  const {
    dropzoneProps: { onPaste: handleFilePaste, ...dragDropProps },
    dropzoneRootRef: editorContainerRef,
    isDragging,
    versionDialog,
  } = useChatEditorDropzone({
    disabled: isImproving,
    editorRef: markdownEditorRef,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { classes } = useStyles({ isDragging });
  /** Last serialized document-chip set, used to bump documentChipVersion only on change. */
  const prevDocumentChipKeyRef = useRef<string | null>(null);
  // Debounced markdown serialization to prevent excessive calls on every keystroke.
  const handleEditorChange = useDebouncedCallback(() => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;

    // A skill chip serializes to its `skill:` link, so a chip-only message is not empty.
    const newText = getDraftMarkdown(editor);
    setCurrentText(newText);

    /**
     * Notify out-of-tree subscribers (the repository "Add to Chat" toggle) when
     * the full-document chip set changes. Only bump on an actual change so we
     * don't re-render those subscribers on every keystroke.
     */
    const chipKey = extractDocumentChipIdsFromEditor(editor).sort().join(',');
    if (chipKey !== prevDocumentChipKeyRef.current) {
      prevDocumentChipKeyRef.current = chipKey;
      $chatThreadState.documentChipVersion += 1;
    }

    writeChatDraft(draftKey, newText);
  }, 100);

  /**
   * Run the draft-sync immediately, bypassing the typing debounce. Programmatic
   * mutations (repository "Add to Chat" / "Remove from Chat" inserting or
   * removing chips) otherwise leave `currentText`, `editorDocumentIds`, access
   * checks, submit enablement, and `documentChipVersion` stale for up to the
   * debounce window. Deliberate chip actions should reflect instantly.
   */
  const flushEditorChange = useCallback(() => {
    handleEditorChange();
    handleEditorChange.flush();
  }, [handleEditorChange]);

  /**
   * Extract document IDs from editor and query their access status.
   * Fires when currentText changes so we capture the latest document IDs.
   * Also re-extracts when editorReady changes (handles restored draft from session storage).
   */
  const editorMentionEntityIds = useStable(
    () => extractMentionEntityIdsFromEditor(markdownEditorRef.current?.getEditor() ?? null),
    [currentText, editorReady]
  );
  const {
    accessRequestStatusContextValue,
    accessStatus,
    checkingMentionAccess,
    hasDeletedMentions,
    hasInaccessibleMentions,
    hasProcessingDocuments,
    mentionEntityContextValue,
  } = useMentionAccess(markdownEditorRef, editorMentionEntityIds);

  const hasStreamingMessage = getHasAssistantMessageWithStatus(
    messages,
    Apollo.AgentMessageStatus.Streaming
  );
  // Stop was clicked but the response is still buffering.
  const hasCancellingMessage = getHasAssistantMessageWithStatus(
    messages,
    Apollo.AgentMessageStatus.Cancelling
  );

  const submitDisabledReason = getSubmitDisabledReason({
    checkingMentionAccess,
    hasDeletedMentions,
    hasInaccessibleMentions,
    hasProcessingDocuments,
    isImproving,
  });

  const sendingRef = useRef(false);
  const handleSubmit = useCallback(async () => {
    if (sendingRef.current) return;
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;

    /**
     * Void inline chips leave Plate's zero-width empty-paragraph marker beside the
     * following text. It is structural editor state, not user-authored prompt content.
     */
    const markdown = normalizeBlankMarkdown(getEditorMarkdown(editor))
      .replaceAll('\u200B', '')
      .trim();
    if (!markdown) return;

    /**
     * Each chip is also in the markdown, as an inline `[name](skill:id/version)` link that
     * keeps its place in the sent message; the server loads the skills from these ids.
     * A pasted copy can repeat a chip, which is sent once. The server refuses a turn over
     * the cap, so check it before the draft is cleared and keep it when the send cannot
     * succeed.
     */
    const chipSkills = dedupePinnedSkills(getPinnedSkillsFromEditor(editor));
    if (chipSkills.length > MAX_PINNED_TURN_SKILLS) {
      enqueueSnackbar(TOO_MANY_SKILLS_MESSAGE, { variant: 'warning' });
      return;
    }

    const submittedDocument = JSON.stringify(editor.children);
    let finished = false;
    const finishSubmission = (confirmedThreadId?: string) => {
      if (finished) return;
      finished = true;
      if (markdownEditorRef.current?.getEditor() !== editor) return;
      if (JSON.stringify(editor.children) === submittedDocument) editor.tf.reset();
      // A new thread mounts another composer; publish the latest draft before it reads storage.
      flushEditorChange();
      if (confirmedThreadId && confirmedThreadId !== threadId)
        writeChatDraft(getChatDraftKey(confirmedThreadId), getDraftMarkdown(editor));
    };
    sendingRef.current = true;
    let succeeded = false;
    try {
      succeeded = await onSubmit(markdown, chipSkills, finishSubmission);
    } catch {
      // A send that throws is reported like a refused one.
    } finally {
      sendingRef.current = false;
    }
    if (!succeeded) {
      enqueueSnackbar(SEND_FAILED_MESSAGE, { variant: 'error' });
      return;
    }
    finishSubmission();
  }, [enqueueSnackbar, flushEditorChange, onSubmit, threadId]);
  const guardedHandleSubmit = useAccessGuard({
    callback: handleSubmit,
    minRole: Apollo.OrgRole.Basic,
  });

  const focusComposer = useCallback(() => {
    if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor());
  }, []);

  const openMentionPicker = useCallback(() => {
    markdownEditorRef.current?.openMentionPicker();
  }, []);

  const openHistory = useCallback(() => {
    // Lets floating siblings (e.g. the artifacts bar) collapse themselves.
    $chatThreadState.inputPopoverOpenCount += 1;
    setHistoryPopoverOpen(true);
  }, []);

  const closeHistory = useCallback(() => {
    setHistoryPopoverOpen(false);
    focusComposer();
  }, [focusComposer]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor) return;

      if (isHotkey('escape', e.nativeEvent)) {
        e.preventDefault();
        editor.tf.setValue([{ children: [{ text: '' }], type: 'p' }]);
        setCurrentText('');
        flushEditorChange();
        // Wait for the DOM to update after setValue before focusing.
        requestAnimationFrame(() => focusEditor(editor, 'start'));
        return;
      }

      if (getDidInsertSpaceAfterLink(e, editor)) return;
      if (!isHotkey('mod+enter', e.nativeEvent)) return;

      e.preventDefault();
      if (hasStreamingMessage || hasCancellingMessage) return;

      if (hasProcessingDocuments) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Documents are still being processed.',
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      if (hasInaccessibleMentions || hasDeletedMentions) {
        dispatch({
          payload: {
            children: <>{renderAccessStatusContent(accessStatus)}</>,
            key: uuid4(),
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      guardedHandleSubmit();
    },
    [
      accessStatus,
      dispatch,
      flushEditorChange,
      guardedHandleSubmit,
      hasCancellingMessage,
      hasDeletedMentions,
      hasInaccessibleMentions,
      hasProcessingDocuments,
      hasStreamingMessage,
    ]
  );

  const insertSelectionMentionV3 = useInsertSelectionMentionV3(markdownEditorRef);

  // Warm the Add menu data when the composer mounts, rather than on menu open.
  useFileStorageConnections({ fetchPolicy: 'cache-first' });

  // Capture the editor before refs detach so navigation also preserves the last keystroke.
  useLayoutEffect(() => {
    const editor = markdownEditorRef.current?.getEditor();
    const save = () => {
      if (editor) writeChatDraft(draftKey, getDraftMarkdown(editor));
    };
    window.addEventListener('beforeunload', save);
    return () => {
      save();
      handleEditorChange.cancel();
      window.removeEventListener('beforeunload', save);
    };
  }, [draftKey, handleEditorChange]);

  // Store threadId in editor options for selection/document mention helpers.
  useEffect(() => {
    const editor = markdownEditorRef.current?.getEditor();
    if (editor) editor.chatThreadId = threadId;
  }, [threadId]);

  useMount(() => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;
    // Mark editor as ready so document IDs are extracted from restored draft
    setEditorReady(true);
    // Position cursor at end if draft was restored, otherwise at start
    focusEditor(editor, currentText ? 'end' : 'start');
  });

  useImperativeHandle(ref, () => ({
    getDocumentChipIds: () => {
      return extractDocumentChipIdsFromEditor(markdownEditorRef.current?.getEditor() ?? null);
    },
    insertMentions: (mentions: TMentionElement[]) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || mentions.length === 0) return false;
      // When called from outside the editor (e.g. the repository "Add to Chat"
      // button or the deliverables bar), editor.selection is null because Slate
      // deselects on blur. Explicitly move to the end so insertNodes has a valid
      // anchor point.
      if (!editor.selection) focusEditor(editor, 'end');
      editor.tf.withoutNormalizing(() => {
        editor.tf.removeNodes({ at: [], match: n => n.type === 'mention_input' });
        const nodes: Descendant[] = mentions.flatMap(mention => [mention, { text: ' ' }]);
        editor.tf.insertNodes(nodes, { select: true });
      });
      focusEditor(editor);
      flushEditorChange();
      return true;
    },
    insertSelectionMentionV3,
    insertSkill: (skill: PinnedSkill, scope?: Apollo.AgentSkillScope) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor) return false;
      const offeredScopes = editor.getOption(BaseSkillElementPlugin, 'offeredScopes') ?? null;
      if (scope && !getIsSkillScopeOffered(scope, offeredScopes)) {
        enqueueSnackbar(SHARED_SKILLS_ONLY_MESSAGE, { variant: 'info' });
        return true;
      }
      editor.tf.select(getIsEditorBlank(editor) ? editor.api.start([]) : editor.api.end([]));
      const result = insertSkillElement(editor, skill);
      if (result !== 'inserted') {
        enqueueSnackbar(SKILL_PIN_REFUSAL_MESSAGES[result], { variant: 'info' });
        return true;
      }
      flushEditorChange();
      focusEditor(editor, 'end');
      return true;
    },
    removeDocumentMentions: (ids: string[]) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || ids.length === 0) return;
      const idSet = new Set(ids);
      /**
       * Only remove full-document chips. Selection / selection-v2 mentions
       * share the same documentId but represent pasted excerpts, so removing
       * the document from chat must not silently strip them.
       */
      removeMentions(
        editor,
        mention => isDocumentMentionElement(mention) && idSet.has(mention.documentId)
      );
      flushEditorChange();
    },
    setPromptDraft: (markdown: string) => {
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || !getIsEditorWithMarkdownApi(editor)) return false;
      editor.tf.setValue(deserializeMarkdownWithMentions(editor, markdown));
      setCurrentText(getEditorMarkdown(editor));
      flushEditorChange();
      focusEditor(editor, 'end');
      return true;
    },
  }));

  return (
    <>
      {versionDialog}
      <div ref={rootRef} className={clsx(classes.root, className)} onClick={focusComposer}>
        {/* Editor container with border - contains everything; ref comes from dragDropProps */}
        <div
          data-editor-picker-anchor
          className={classes.editorContainer}
          style={{ position: 'relative' }}
          {...dragDropProps}
        >
          {isDragging && (
            <div className={classes.dropOverlay}>
              <UploadIcon className={classes.dropOverlayIcon} />
              <Typography className={classes.dropOverlayText} variant="body1">
                Drop files to upload
              </Typography>
            </div>
          )}

          {/* Toolbar and textarea, without a border: the container above provides it */}
          <MentionEntityContext.Provider value={mentionEntityContextValue}>
            <AccessRequestStatusContext.Provider value={accessRequestStatusContextValue}>
              <AlineMarkdownEditor
                ref={markdownEditorRef}
                autoFocus
                commandPlaceholder
                noBorder
                disabled={isImproving}
                extraPlugins={chatEditorPlugins}
                maxHeight={300}
                mentions={{
                  allowFileUpload: true,
                  allowIntakeFormMention: Boolean(visibleContext?.workflowId),
                  allowIntegrations: true,
                  quickTypes: DOCUMENT_QUICK_TYPES,
                }}
                minHeight={100}
                readOnly={isImproving}
                topContent={
                  <ChatSkillAudience threadId={threadId} visibleContext={visibleContext} />
                }
                value={currentText}
                onChange={handleEditorChange}
                onKeyDown={handleKeyDown}
                onPaste={handleFilePaste}
              />
            </AccessRequestStatusContext.Provider>
          </MentionEntityContext.Provider>

          <div className={classes.buttonContainer}>
            <AddContextButton disabled={isImproving} onClick={openMentionPicker} />
            <HistoryButton
              className={classes.iconButton}
              disabled={isImproving}
              isOpen={historyPopoverOpen}
              onClick={openHistory}
            />
            <ImprovePromptButton
              className={classes.iconButton}
              hasText={hasText}
              isImproving={isImproving}
              onClick={improvePrompt}
            />
            <Spacer />
            <ChatSubmitButton
              accessStatus={accessStatus}
              disabledReason={submitDisabledReason}
              messages={messages}
              prompt={currentText}
              threadId={threadId}
              onSubmit={guardedHandleSubmit}
            />
          </div>
        </div>
      </div>
      <ChatEditorHistory
        anchorEl={editorContainerRef.current}
        open={historyPopoverOpen}
        onClose={closeHistory}
        onNavigateToThread={onNavigateToThread}
        onThreadDeleted={handleThreadDeleted}
      />
    </>
  );
});

ChatPanelInputEditor.displayName = 'ChatPanelInputEditor';

export const ChatPanelInput = forwardRef<
  ChatPanelInputRef,
  Omit<React.ComponentProps<typeof ChatPanelInputEditor>, 'draftKey'>
>(function ChatPanelInput(props, ref) {
  const { newChatDraftId } = useSnapshot($chatThreadState);
  const draftKey = getChatDraftKey(props.threadId, newChatDraftId);
  return <ChatPanelInputEditor key={draftKey} {...props} ref={ref} draftKey={draftKey} />;
});

/**
 * Hydrates the access state of the documents mentioned in the draft, and the
 * handlers that request access to inaccessible ones or clear deleted ones.
 */
function useMentionAccess(
  markdownEditorRef: EditorRef,
  mentionEntityIds: Parameters<typeof useMentionEntitiesQuery>[0]
) {
  const {
    deletedDocumentIds,
    hasDeletedMentions,
    hasInaccessibleMentions,
    hasProcessingDocuments,
    inaccessibleDocumentIds,
    loading: checkingMentionAccess,
    mentionEntityContextValue,
  } = useMentionEntitiesQuery(mentionEntityIds);
  const { requestAccess } = useRequestAccess();

  const {
    refetch: refetchAccessRequestStatus,
    statusMap: accessRequestStatusMap,
    updateStatus,
  } = useAccessRequestStatus(inaccessibleDocumentIds);
  const accessRequestStatusContextValue = useMemo(
    () => ({
      refetch: refetchAccessRequestStatus,
      statusMap: accessRequestStatusMap,
      updateStatus,
    }),
    [accessRequestStatusMap, refetchAccessRequestStatus, updateStatus]
  );

  const allAccessRequested = useMemo(() => {
    if (inaccessibleDocumentIds.length === 0) return false;
    return inaccessibleDocumentIds.every(
      id => accessRequestStatusMap.get(id) === Apollo.AccessRequestStatus.Pending
    );
  }, [inaccessibleDocumentIds, accessRequestStatusMap]);

  const handleRequestAccess = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const result = await requestAccess(inaccessibleDocumentIds);
      // Update local state with returned data (avoids extra network request)
      if (result) updateStatus(result);
    },
    [inaccessibleDocumentIds, requestAccess, updateStatus]
  );

  const handleClearDeleted = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const editor = markdownEditorRef.current?.getEditor();
      if (!editor || deletedDocumentIds.length === 0) return;
      const deletedSet = new Set(deletedDocumentIds);
      removeMentions(
        editor,
        mention =>
          'documentId' in mention &&
          typeof mention.documentId === 'string' &&
          deletedSet.has(mention.documentId)
      );
    },
    [deletedDocumentIds, markdownEditorRef]
  );

  const accessStatus: AccessStatusContentProps = useMemo(
    () => ({
      accessRequested: allAccessRequested,
      deletedCount: deletedDocumentIds.length,
      inaccessibleCount: inaccessibleDocumentIds.length,
      onClearDeleted: handleClearDeleted,
      onRequestAccess: handleRequestAccess,
    }),
    [
      allAccessRequested,
      deletedDocumentIds.length,
      handleClearDeleted,
      handleRequestAccess,
      inaccessibleDocumentIds.length,
    ]
  );

  return {
    accessRequestStatusContextValue,
    accessStatus,
    checkingMentionAccess,
    hasDeletedMentions,
    hasInaccessibleMentions,
    hasProcessingDocuments,
    mentionEntityContextValue,
  };
}

/** Rewrites the draft with AI, streaming the improved text back into the editor. */
function useImprovePrompt(markdownEditorRef: EditorRef) {
  const dispatch = useNotificationDispatch();
  const [isImproving, setIsImproving] = useState(false);
  const [improvePromptMutation] = Apollo.useImprovePromptMutation();
  const [improveRequestId, setImproveRequestId] = useState<string | null>(null);

  const handleComplete = useCallback(() => {
    setImproveRequestId(null);
    setIsImproving(false);
    if (markdownEditorRef.current) focusEditor(markdownEditorRef.current.getEditor(), 'end');
  }, [markdownEditorRef]);

  useStreamImprovePrompt({
    editorRef: markdownEditorRef,
    improveRequestId,
    onComplete: handleComplete,
    onStreamStart: () => setIsImproving(true),
  });

  const improvePrompt = useCallback(async () => {
    const editor = markdownEditorRef.current?.getEditor();
    if (!editor) return;

    const markdown = getDraftMarkdown(editor);
    if (!markdown) return;

    const requestId = uuid4();
    setIsImproving(true);
    setImproveRequestId(requestId);

    try {
      await improvePromptMutation({
        variables: { input: { originalText: markdown, requestId } },
      });
    } catch {
      setIsImproving(false);
      setImproveRequestId(null);
      dispatch({
        payload: {
          key: uuid4(),
          message: 'Failed to improve prompt. Please try again.',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    }
  }, [dispatch, improvePromptMutation, markdownEditorRef]);

  return { improvePrompt, isImproving };
}

function ChatSubmitButton({
  accessStatus,
  disabledReason,
  messages,
  onSubmit,
  prompt,
  threadId,
}: {
  accessStatus: AccessStatusContentProps;
  disabledReason: string;
  messages: SubmitButtonMessages;
  onSubmit: () => void;
  prompt: string;
  threadId: UUID | null;
}) {
  const theme = useTheme();
  const [stopChatMessageStream] = Apollo.useStopChatMessageStreamMutation();

  // Track when user has clicked stop - stays true until streaming actually ends
  const [isStopping, setIsStopping] = useState(false);

  const isStreaming =
    !!threadId && getHasAssistantMessageWithStatus(messages, Apollo.AgentMessageStatus.Streaming);

  // Check if all assistant messages have reached a terminal state (Complete or Cancelled)
  const allMessagesFinished =
    !threadId ||
    messages
      .filter(m => m.role === Apollo.AgentMessageRole.Assistant)
      .every(
        m =>
          m.status === Apollo.AgentMessageStatus.Complete ||
          m.status === Apollo.AgentMessageStatus.Cancelled
      );

  // Reset isStopping only when messages have actually finished (terminal state)
  useEffect(() => {
    if (allMessagesFinished && isStopping) setIsStopping(false);
  }, [allMessagesFinished, isStopping]);

  if (isStreaming || isStopping)
    return (
      <AlineButton
        color="primary"
        disabled={isStopping}
        style={{ padding: theme.spacing(0, 1.5) }}
        variant="outlined"
        onClick={async (e: React.MouseEvent) => {
          e.preventDefault();
          if (isStreaming && !isStopping) {
            setIsStopping(true);
            await stopChatMessageStream({ variables: { threadId } });
          }
        }}
      >
        <SubmitButtonContent
          icon={<StopIcon height={16} stroke="currentColor" width={16} />}
          label={isStopping ? 'Stopping' : 'Stop'}
        />
      </AlineButton>
    );

  const tooltip = disabledReason
    ? (renderAccessStatusContent(accessStatus) ?? disabledReason)
    : 'Send message (⌘+Enter)';

  return (
    <AlineTooltip arrow placement="top" title={tooltip}>
      <div>
        <AlineButton
          color="primary"
          disabled={!prompt.trim() || Boolean(disabledReason)}
          style={{ padding: theme.spacing(0, 1.5) }}
          variant="contained"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <SubmitButtonContent icon={<ArrowUpIcon height={16} width={16} />} label="Send" />
        </AlineButton>
      </div>
    </AlineTooltip>
  );
}

function SubmitButtonContent({ icon, label }: { icon: React.ReactNode; label: string }) {
  const theme = useTheme();
  return (
    <>
      <Typography noWrap color="inherit" component="span" sx={{ fontWeight: 'bold' }}>
        {label}
      </Typography>
      <Box style={{ alignItems: 'center', display: 'flex', marginLeft: theme.spacing(0.5) }}>
        {icon}
      </Box>
    </>
  );
}

function HistoryButton({
  className,
  disabled,
  isOpen,
  onClick,
}: {
  className: string;
  disabled: boolean;
  isOpen: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  return (
    <AlineTooltip arrow placement="top" title="View chat history">
      <div>
        <IconButton
          className={className}
          disabled={disabled}
          size="small"
          style={
            isOpen
              ? {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                }
              : undefined
          }
          onClick={onClick}
        >
          <RevisionHistoryIcon style={{ height: 20, width: 20 }} />
        </IconButton>
      </div>
    </AlineTooltip>
  );
}

function ImprovePromptButton({
  className,
  hasText,
  isImproving,
  onClick,
}: {
  className: string;
  hasText: boolean;
  isImproving: boolean;
  onClick: () => void;
}) {
  return (
    <AlineTooltip arrow placement="top" title={getImprovePromptTooltip({ hasText, isImproving })}>
      <div>
        <IconButton
          aria-busy={isImproving}
          aria-label="Improve prompt"
          className={className}
          color="primary"
          disabled={isImproving || !hasText}
          size="small"
          onClick={onClick}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: 18,
              justifyContent: 'center',
              width: 18,
            }}
          >
            <ImprovePromptIcon hasText={hasText} isImproving={isImproving} />
          </Box>
        </IconButton>
      </div>
    </AlineTooltip>
  );
}

function ImprovePromptIcon({ hasText, isImproving }: { hasText: boolean; isImproving: boolean }) {
  const theme = useTheme();
  if (isImproving) return <PulseIcon variant={PulseIconVariant.LoadingPrimary} />;
  return (
    <SmallAiSparkleIcon
      fill={hasText ? theme.palette.primary.main : theme.palette.action.disabled}
    />
  );
}

function getImprovePromptTooltip({
  hasText,
  isImproving,
}: {
  hasText: boolean;
  isImproving: boolean;
}) {
  if (isImproving) return 'Improving prompt...';
  if (hasText) return 'Improve prompt';
  return 'Start typing to improve your prompt';
}

function getSubmitDisabledReason({
  checkingMentionAccess,
  hasDeletedMentions,
  hasInaccessibleMentions,
  hasProcessingDocuments,
  isImproving,
}: {
  checkingMentionAccess: boolean;
  hasDeletedMentions: boolean;
  hasInaccessibleMentions: boolean;
  hasProcessingDocuments: boolean;
  isImproving: boolean;
}) {
  if (isImproving) return 'Improving prompt...';
  if (checkingMentionAccess) return 'Checking document access...';
  if (hasProcessingDocuments) return 'Documents are still being processed';
  if (hasInaccessibleMentions)
    return 'You do not have access to some documents mentioned in the chat';
  if (hasDeletedMentions) return 'Some documents mentioned in the chat have been deleted';
  return '';
}

function getHasAssistantMessageWithStatus(
  messages: SubmitButtonMessages,
  status: Apollo.AgentMessageStatus
) {
  return messages.some(m => m.role === Apollo.AgentMessageRole.Assistant && m.status === status);
}

/** The draft as stored: trimmed markdown, with a blank editor stored as ''. */
function getDraftMarkdown(editor: PlateEditor) {
  return normalizeBlankMarkdown(getEditorMarkdown(editor).trim());
}

/** Removes every mention chip that matches, last first so earlier paths stay valid. */
function removeMentions(editor: PlateEditor, shouldRemove: (mention: TMentionElement) => boolean) {
  const entries = [...editor.api.nodes({ at: [], match: { type: 'mention' } })].reverse();
  for (const [node, path] of entries) {
    if (getIsMentionElement(node) && shouldRemove(node)) editor.tf.removeNodes({ at: path });
  }
}

/** Space typed inside a link moves the caret past the link before inserting the space. */
function getDidInsertSpaceAfterLink(e: React.KeyboardEvent, editor: PlateEditor) {
  if (e.key !== ' ' || !editor.selection) return false;
  try {
    if (!editor.api.node({ match: { type: 'a' } })) return false;
    e.preventDefault();
    editor.tf.move({ distance: 1, unit: 'offset' });
    editor.tf.insertText(' ');
    return true;
  } catch {
    // If any error, just let space work normally.
    return false;
  }
}
